/**
 * The home's plan section: what we are doing, what we are not, and who
 * agreed — and, when one is being made, the next plan and where it stands
 * (owner rulings 2026-10-16). Always drawn: when there is no plan it says so.
 *
 * Data comes from useHomePlan; the answers are phrased in lib/plan.ts. The
 * viewer's own items (owned, or handed to them) are marked in place.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { RankMark } from '@/components/prioritization/RankMark';
import { useActorDisplayMap } from '../../hooks/prioritization/useActorDisplayMap';
import { formatRelativeTime } from '../../i18n/formatters';
import { LoopChangeRow } from './LoopChangeRow';
import { loopInPlay } from './lib/sections';
import {
  isMine,
  shortDate,
  splitAtLine,
  whatWouldChange,
  whoAgreedTrail,
  type ChangeRow,
  type CycleStep,
  type ItemPeople,
} from './lib/plan';
import type { CycleInMaking, HomePlanData, PlanInForceView } from './useHomePlan';
import { itemInContextHref } from '@/lib/itemCycleStanding';

const EYEBROW = 'font-mono text-xs uppercase tracking-[0.16em] text-[var(--text-ter)]';
const SUBHEAD = 'font-mono text-xs uppercase tracking-[0.14em] text-[var(--text-pri)] mt-7 mb-2';
const CARD = 'border border-[var(--rule)] bg-[var(--bg-raised)] p-5 sm:p-7';
const MORE_LINK = 'text-sm text-[var(--text-ter)] underline underline-offset-4 hover:text-[var(--text-pri)]';

function Tag({ tone, children }: { tone: 'agreed' | 'making' | 'none'; children: React.ReactNode }) {
  const color = tone === 'agreed' ? 'var(--green-text)' : tone === 'making' ? 'var(--mustard)' : 'var(--coral-text)';
  return (
    <span className="shrink-0 border px-2 py-0.5 font-mono text-xs uppercase tracking-[0.12em]" style={{ color, borderColor: color }}>
      {children}
    </span>
  );
}

function dayRange(range: { start: string | null; end: string | null } | null): string | null {
  if (!range?.start && !range?.end) return null;
  const fmt = (d: string | null) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null);
  const a = fmt(range.start);
  const b = fmt(range.end);
  return a && b ? `${a} – ${b}` : a ?? b;
}

/** One ranked item: its mark, its name, and whose it is. Yours stands out in place. */
function RankRow({
  itemId,
  planCycleId,
  title,
  rank,
  official,
  above,
  people,
  viewerId,
  ownerNames,
  extra,
  className = '',
}: {
  itemId: string;
  /** The plan this row is in: the item page opens on that plan's standing. */
  planCycleId?: string;
  title: string;
  rank: number | null;
  official: boolean;
  above: boolean | null;
  people: ItemPeople | undefined;
  viewerId: string | null;
  ownerNames: Record<string, string>;
  extra?: string | null;
  className?: string;
}) {
  const mine = isMine(people, viewerId);
  const owner = people?.ownerId ? ownerNames[String(people.ownerId)] ?? null : null;
  const gray = above === false;
  return (
    <li className={className}>
      <Link
        to={itemInContextHref(itemId, { planCycleId })}
        data-testid={mine ? 'home-plan-row-mine' : 'home-plan-row'}
        className={`-mx-2 flex items-center gap-3 border-t border-[var(--rule)] px-2 py-2 hover:bg-[var(--hover-tint)] ${mine ? 'bg-[var(--mustard-dim)]' : ''}`}
      >
        <RankMark rank={rank} official={official} aboveTheLine={above} size="sm" />
        <span className={`min-w-0 flex-1 ${gray ? 'text-sm text-[var(--text-dim)]' : 'text-[15px] text-[var(--text-pri)]'} ${mine ? 'font-semibold' : ''}`}>
          {title}
        </span>
        {extra && <span className="shrink-0 text-xs text-[var(--text-ter)]">{extra}</span>}
        {mine ? (
          <span className="shrink-0 font-mono text-xs uppercase tracking-[0.1em] text-[var(--mustard)]">Yours</span>
        ) : (
          owner && <span className="hidden shrink-0 text-xs text-[var(--text-ter)] sm:inline">{owner}</span>
        )}
      </Link>
    </li>
  );
}

function TheLine({ label = 'The line', dashed = false }: { label?: string; dashed?: boolean }) {
  const rule = `flex-1 border-t-2 border-[var(--text-pri)] ${dashed ? 'border-dashed' : ''}`;
  return (
    <div role="separator" aria-label={label} className="mt-1 flex items-center gap-2.5">
      <span className={rule} />
      <span className="font-mono text-xs uppercase tracking-[0.14em] text-[var(--text-pri)]">{label}</span>
      <span className={rule} />
    </div>
  );
}

function Steps({ steps }: { steps: CycleStep[] }) {
  return (
    <ol aria-label="How far it has gotten" className="mt-5 grid gap-1" style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
      {steps.map((s) => (
        <li
          key={s.key}
          aria-current={s.state === 'now' ? 'step' : undefined}
          className={`border-t-[3px] pt-2 ${s.state === 'done' ? 'border-[var(--text-pri)]' : s.state === 'now' ? 'border-[var(--mustard)]' : 'border-dashed border-[var(--rule-bright)]'}`}
        >
          <span
            className={`block text-xs ${s.state === 'done' ? 'font-medium text-[var(--text-pri)]' : s.state === 'now' ? 'font-semibold text-[var(--mustard)]' : 'text-[var(--text-dim)]'}`}
          >
            {s.label}
          </span>
          {s.note && <span className={`block text-xs ${s.state === 'now' ? 'text-[var(--mustard)]' : 'text-[var(--text-ter)]'}`}>{s.note}</span>}
        </li>
      ))}
    </ol>
  );
}

function ChangeGroup({ title, rows, people, viewerId, ownerNames, kind }: {
  title: string;
  rows: ChangeRow[];
  people: Record<string, ItemPeople>;
  viewerId: string | null;
  ownerNames: Record<string, string>;
  kind: 'done' | 'new' | 'dropping';
}) {
  if (rows.length === 0) return null;
  return (
    <>
      <p className="mb-1 mt-4 text-sm font-semibold text-[var(--text-sec)]">{title}</p>
      <ol>
        {rows.map((r) => (
          <RankRow
            key={r.itemId}
            itemId={r.itemId}
            title={r.title}
            rank={kind === 'done' ? r.wasRank : r.newRank}
            official={kind === 'done'}
            above={kind === 'dropping' ? false : kind === 'done' ? null : true}
            people={people[r.itemId]}
            viewerId={viewerId}
            ownerNames={ownerNames}
            extra={kind === 'done' ? 'delivered' : r.wasRank != null ? `was ${r.wasRank}` : 'new to the plan'}
          />
        ))}
      </ol>
    </>
  );
}

// ── The plan in force ──────────────────────────────────────────────────────

function PlanInForceCard({
  plan,
  data,
  viewerId,
  names,
  ballHolderId,
  ballSince,
  compact,
}: {
  plan: PlanInForceView;
  data: HomePlanData;
  viewerId: string | null;
  names: Record<string, string>;
  ballHolderId: string | null;
  ballSince: string | null;
  compact: boolean;
}) {
  const { doing, notDoing } = splitAtLine(plan.ranks);
  const shownDoing = compact ? doing.slice(0, 5) : doing;
  const by = plan.authorizedById ? names[plan.authorizedById] ?? null : plan.authorizedByText;
  const trail = whoAgreedTrail({
    session: plan.trail.session
      ? { ...plan.trail.session, facilitatorName: plan.trail.session.facilitatorId ? names[plan.trail.session.facilitatorId] ?? null : null }
      : null,
    turns: plan.trail.turns.map((t) => ({
      submittedAt: t.submittedAt,
      submittedByName: t.submittedBy ? names[t.submittedBy] ?? null : null,
      resolvedAt: t.resolvedAt,
      resolvedByName: t.resolvedBy ? names[t.resolvedBy] ?? null : null,
      decision: t.decision,
    })),
    authorization: { at: plan.authorizedAt, version: plan.version, byLabel: by },
  });

  return (
    <article className={CARD} data-testid="home-plan-in-force">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-heading text-2xl font-semibold leading-tight text-[var(--text-pri)]" style={{ color: 'var(--text-pri)' }}>
          <Link to={`/planning/plans/${plan.planCycleId}`} className="hover:underline">{plan.name}</Link>
        </h3>
        <Tag tone="agreed">Agreed</Tag>
      </div>
      {plan.commitment && !compact && (
        <p className="mt-3 font-display text-lg italic leading-snug text-[var(--text-pri)]">“{plan.commitment}”</p>
      )}
      <p className="mt-2 text-sm text-[var(--text-sec)]">
        {[plan.version != null ? `Version ${plan.version}` : null, plan.authorizedAt ? `authorized ${shortDate(plan.authorizedAt)}${by ? ` by ${by}` : ''}` : null]
          .filter(Boolean)
          .join(', ')}
        {plan.untilOn ? `. In force until ${shortDate(plan.untilOn)}, when the next plan takes over.` : '.'}
      </p>

      <h4 className={SUBHEAD}>What we are doing · {doing.length}</h4>
      <ol>
        {shownDoing.map((r, i) => (
          <RankRow
            key={r.itemId}
            itemId={r.itemId}
            title={r.title}
            rank={r.rank}
            planCycleId={plan.planCycleId}
            official
            above
            people={data.itemPeople[r.itemId]}
            viewerId={viewerId}
            ownerNames={names}
            className={!compact && i >= 5 ? 'hidden lg:block' : ''}
          />
        ))}
      </ol>
      {!compact && doing.length > 5 && (
        <Link to={`/planning/plans/${plan.planCycleId}`} className={`mt-2 block lg:hidden ${MORE_LINK}`}>
          And {doing.length - 5} more we are doing
        </Link>
      )}
      {compact && doing.length > shownDoing.length && (
        <Link to={`/planning/plans/${plan.planCycleId}`} className={`mt-2 block ${MORE_LINK}`}>
          And {doing.length - shownDoing.length} more we are doing
        </Link>
      )}

      {notDoing.length > 0 && (
        <>
          <TheLine />
          <h4 className={SUBHEAD}>What we are not doing · {notDoing.length}</h4>
          {compact ? (
            <p className="text-sm text-[var(--text-dim)]">{notDoing.map((r) => r.title).join(' · ')}</p>
          ) : (
            <ol>
              {notDoing.map((r) => (
                <RankRow
                  key={r.itemId}
                  itemId={r.itemId}
                  title={r.title}
                  rank={r.rank}
                  planCycleId={plan.planCycleId}
                  official
                  above={false}
                  people={data.itemPeople[r.itemId]}
                  viewerId={viewerId}
                  ownerNames={names}
                />
              ))}
            </ol>
          )}
        </>
      )}

      {!compact && trail.length > 0 && (
        <>
          <h4 className={SUBHEAD}>Who agreed</h4>
          <ol>
            {trail.map((line, i) => (
              <li key={i} className="flex gap-3.5 border-t border-[var(--rule)] py-2.5">
                <span className="w-14 shrink-0 pt-0.5 font-mono text-xs text-[var(--text-ter)]">{line.when ?? ''}</span>
                <span className="text-[15px] leading-snug text-[var(--text-pri)]">{line.text}</span>
              </li>
            ))}
          </ol>
        </>
      )}

      {!compact && (
        <>
          <h4 className={SUBHEAD}>Since it was agreed</h4>
          <p className="bg-[var(--surface)] px-3.5 py-3 text-[15px] leading-relaxed text-[var(--text-pri)]" data-testid="home-plan-since">
            {plan.since === null
              ? 'What changed since could not be read.'
              : plan.since.items === 0
                ? 'Nothing in it has changed.'
                : `${plan.since.items === 1 ? 'One of its items has' : `${plan.since.items} of its items have`} changed${plan.since.notDoing > 0 ? ` (${plan.since.doing} we are doing, ${plan.since.notDoing} we are not)` : ''}${plan.since.lastAt ? `, most recently ${formatRelativeTime(plan.since.lastAt)}` : ''}${plan.since.mostlyBy && names[plan.since.mostlyBy] ? `, mostly by ${names[plan.since.mostlyBy]}` : ''}.`}{' '}
            {plan.since && plan.since.items > 0 && (
              <Link to={`/planning/plans/${plan.planCycleId}`} className="underline underline-offset-4">
                See what changed
              </Link>
            )}
          </p>
        </>
      )}

      <div className="mt-6 flex flex-wrap items-baseline justify-between gap-3 border-t border-[var(--rule)] pt-4 text-sm text-[var(--text-sec)]">
        <span>
          {plan.sourceCycle && (
            <>
              From <Link to={`/prioritization/cycles/${plan.sourceCycle.id}`} className="underline underline-offset-4">{plan.sourceCycle.name}</Link>
              {ballHolderId && names[ballHolderId] ? ` · the ball is with ${names[ballHolderId]}${ballSince ? `, since ${shortDate(ballSince)}` : ''}` : ''}
            </>
          )}
        </span>
        <Link to={`/planning/plans/${plan.planCycleId}`} className="underline underline-offset-4">See the whole plan →</Link>
      </div>
    </article>
  );
}

// ── A plan being made ──────────────────────────────────────────────────────

function MakingCard({
  making,
  data,
  viewerId,
  names,
  asNext,
  showSinceReview,
  loopId,
}: {
  making: CycleInMaking;
  data: HomePlanData;
  viewerId: string | null;
  names: Record<string, string>;
  asNext: boolean;
  showSinceReview: boolean;
  loopId: string | null;
}) {
  const { cycle } = making;
  const range = dayRange(making.dateRange);
  const change = useMemo(
    () => (asNext && data.inForce && making.proposed ? whatWouldChange(data.inForce.ranks, making.proposed.rows, making.proposed.cut, data.delivered) : null),
    [asNext, data.inForce, making.proposed, data.delivered],
  );
  const proposed = making.proposed;
  const cut = proposed?.cut ?? null;
  const ball = making.ballHolderId ? names[making.ballHolderId] ?? null : null;

  return (
    <article className={`${CARD} ${asNext ? 'border-dashed border-[var(--rule-bright)]' : ''}`} data-testid="home-plan-making">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-heading text-xl font-semibold leading-tight text-[var(--text-pri)]" style={{ color: 'var(--text-pri)' }}>
          <Link to={`/prioritization/cycles/${cycle.id}`} className="hover:underline">{cycle.name}</Link>
        </h3>
        <Tag tone="making">{making.designed ? 'Being made' : 'Not designed yet'}</Tag>
      </div>
      <p className="mt-3 text-[15px] leading-relaxed text-[var(--text-pri)]">
        {asNext ? 'It takes over once it is authorized.' : 'There is no plan in force until this one is authorized.'}
        {range ? ` It covers ${range}.` : ''}
      </p>

      {!making.designed && (
        <p className="mt-3 text-[15px] leading-relaxed text-[var(--text-sec)]">
          It has been started as an empty cycle. Who is in it, and which parts of the organization it passes through, are still to be decided.{' '}
          <Link to={`/prioritization/cycles/${cycle.id}/designer`} className="underline underline-offset-4">Design it</Link>
        </p>
      )}

      {making.steps && <Steps steps={making.steps} />}

      {change && (
        <>
          <h4 className={SUBHEAD}>What would change</h4>
          <p className="text-sm text-[var(--text-ter)]">The proposed ranking, against the plan in force.</p>
          <ChangeGroup title="Leaving the plan, delivered" rows={change.doneAndLeaving} people={data.itemPeople} viewerId={viewerId} ownerNames={names} kind="done" />
          <ChangeGroup title="New above the line" rows={change.newAboveTheLine} people={data.itemPeople} viewerId={viewerId} ownerNames={names} kind="new" />
          <ChangeGroup title="Dropping below the line" rows={change.droppingBelow} people={data.itemPeople} viewerId={viewerId} ownerNames={names} kind="dropping" />
          <p className="mt-3 text-sm text-[var(--text-sec)]">
            {change.staying === 0 ? 'Nothing else stays above the line.' : `${change.staying === 1 ? 'One other stays' : `The other ${change.staying} stay`} above the line.`}
          </p>
        </>
      )}

      {!change && proposed && (
        <>
          <h4 className={SUBHEAD}>Proposed: what we would do{cut != null ? ` · ${Math.min(cut, proposed.rows.length)}` : ''}</h4>
          <ol>
            {proposed.rows.slice(0, cut ?? proposed.rows.length).slice(0, 12).map((r, i) => (
              <RankRow
                key={r.itemId}
                itemId={r.itemId}
                title={r.title}
                rank={i + 1}
                official={false}
                above={cut != null ? true : null}
                people={data.itemPeople[r.itemId]}
                viewerId={viewerId}
                ownerNames={names}
              />
            ))}
          </ol>
          {cut != null && proposed.rows.length > cut && (
            <>
              <TheLine label="Proposed line" dashed />
              <p className="mt-2 text-sm text-[var(--text-dim)]">
                Proposed not to do ({proposed.rows.length - cut}): {proposed.rows.slice(cut).map((r) => r.title).join(' · ')}
              </p>
            </>
          )}
          {cut == null && <p className="mt-2 text-sm text-[var(--text-ter)]">No line has been drawn yet.</p>}
        </>
      )}

      {!proposed && making.designed && (
        <>
          <h4 className={SUBHEAD}>On the list · {making.listCount}</h4>
          <p className="text-sm text-[var(--text-sec)]">Nothing is ranked yet, so there is no “doing” list.</p>
          {making.onTheList.length > 0 && (
            <ul className="mt-2">
              {making.onTheList.map((r) => (
                <RankRow key={r.itemId} itemId={r.itemId} title={r.title} rank={null} official={false} above={null} people={data.itemPeople[r.itemId]} viewerId={viewerId} ownerNames={names} />
              ))}
            </ul>
          )}
        </>
      )}

      {showSinceReview && loopId && (
        <ul className="mt-4">
          <LoopChangeRow
            loopId={loopId}
            title="Since the last review"
            href={`/prioritization/cascade/${cycle.id}`}
            rowClassName="block border-t border-[var(--rule)] py-2.5 hover:bg-[var(--hover-tint)]"
            onHasChange={noop}
          />
        </ul>
      )}

      <div className="mt-6 flex flex-wrap items-baseline justify-between gap-3 border-t border-[var(--rule)] pt-4 text-sm text-[var(--text-sec)]">
        <span>{ball ? `The ball is with ${ball}${making.ballSince ? `, since ${shortDate(making.ballSince)}` : ''}` : ''}</span>
        <Link to={`/prioritization/cycles/${cycle.id}`} className="underline underline-offset-4">Open the cycle →</Link>
      </div>
      {data.alsoMaking.length > 0 && (
        <p className="mt-3 text-sm text-[var(--text-ter)]">
          Also being made:{' '}
          {data.alsoMaking.map((c, i) => (
            <span key={c.id}>
              {i > 0 && ', '}
              <Link to={`/prioritization/cycles/${c.id}`} className="underline underline-offset-4">{c.name}</Link>
            </span>
          ))}
        </p>
      )}
    </article>
  );
}

const noop = () => {};

// ── No plan ────────────────────────────────────────────────────────────────

function NoPlanCard({ itemsWaiting, canStart, startsWith }: { itemsWaiting: number | null; canStart: boolean; startsWith: string | null }) {
  return (
    <article className={CARD} data-testid="home-plan-none">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-heading text-2xl font-semibold leading-tight text-[var(--text-pri)]" style={{ color: 'var(--text-pri)' }}>No plan right now</h3>
        <Tag tone="none">No plan</Tag>
      </div>
      <p className="mt-3 text-base leading-relaxed text-[var(--text-pri)]">
        No plan is in force, and none is being made, so nobody can say yet what we are doing.
      </p>
      {itemsWaiting != null && itemsWaiting > 0 && (
        <p className="mt-2 text-[15px] text-[var(--text-sec)]">
          {itemsWaiting === 1 ? 'One item is' : `${itemsWaiting.toLocaleString('en-US')} items are`} waiting on the list.
        </p>
      )}
      {!canStart && (
        <>
          <p className="mt-2 text-[15px] text-[var(--text-sec)]">Starting one is up to {startsWith ?? 'your workspace admin'}.</p>
          <Link
            to="/prioritization/intake/submit"
            className="mt-5 inline-flex min-h-[44px] items-center border border-[var(--text-pri)] px-4 font-mono text-sm uppercase tracking-[0.08em] text-[var(--text-pri)] hover:bg-[var(--hover-tint)]"
          >
            Add an item for the next plan
          </Link>
        </>
      )}
    </article>
  );
}

// ── The section ────────────────────────────────────────────────────────────

export interface PlanSectionProps {
  data: HomePlanData;
  viewerId: string | null;
  canStart: boolean;
  /** Who to name when the viewer cannot start a plan. */
  startsWith: string | null;
  /** Active items in the workspace; null when not known. */
  itemsWaiting: number | null;
  /** The viewer's roles care about who holds the ball / what changed since the last review. */
  showBall: boolean;
  showSinceReview: boolean;
  /** Loops in the stores, to find a cycle's loop in play and its ball. */
  loops: Array<{ id: string; cycleId: string; state: string; ballHolderUserId?: string | null; ballClaimedAt?: string | null }>;
  /** The guided home: the plan's top five and what it is not doing, nothing more. */
  compact?: boolean;
}

export function PlanSection({ data, viewerId, canStart, startsWith, itemsWaiting, showBall, showSinceReview, loops, compact = false }: PlanSectionProps) {
  const sourceLoop = data.inForce?.sourceCycle
    ? loops.find((l) => l.cycleId === data.inForce!.sourceCycle!.id && (loopInPlay(l.state) || l.state.toLowerCase() === 'accepted_provisional'))
    : undefined;
  const makingLoop = data.making ? loops.find((l) => l.cycleId === data.making!.cycle.id && loopInPlay(l.state)) : undefined;

  const ids = useMemo(() => {
    const set = new Set<string>();
    for (const p of Object.values(data.itemPeople)) {
      if (p.ownerId) set.add(String(p.ownerId));
      if (p.delegateId) set.add(String(p.delegateId));
    }
    const plan = data.inForce;
    if (plan?.authorizedById) set.add(plan.authorizedById);
    if (plan?.trail.session?.facilitatorId) set.add(plan.trail.session.facilitatorId);
    for (const t of plan?.trail.turns ?? []) {
      if (t.submittedBy) set.add(t.submittedBy);
      if (t.resolvedBy) set.add(t.resolvedBy);
    }
    if (plan?.since?.mostlyBy) set.add(plan.since.mostlyBy);
    if (data.making?.ballHolderId) set.add(data.making.ballHolderId);
    if (sourceLoop?.ballHolderUserId) set.add(sourceLoop.ballHolderUserId);
    return [...set];
  }, [data, sourceLoop]);
  const names = useActorDisplayMap(ids);

  let body: React.ReactNode;
  if ((data.loading && !data.inForce && !data.making) || (!data.inForce && !data.making && !data.unread && data.makingLoading)) {
    body = <p className="text-[var(--text-sec)]" aria-busy="true">Loading the plan…</p>;
  } else if (data.unread) {
    body = <p className="text-[var(--text-sec)]">The plan could not be read just now. Reload the page to try again.</p>;
  } else if (data.inForce) {
    body = (
      <div className="flex flex-col gap-8">
        <PlanInForceCard
          plan={data.inForce}
          data={data}
          viewerId={viewerId}
          names={names}
          ballHolderId={showBall ? sourceLoop?.ballHolderUserId ?? null : null}
          ballSince={sourceLoop?.ballClaimedAt ?? null}
          compact={compact}
        />
        {data.making && !compact && (
          <div>
            <p className={`${EYEBROW} mb-3`}>The next plan</p>
            <MakingCard making={data.making} data={data} viewerId={viewerId} names={names} asNext showSinceReview={showSinceReview} loopId={makingLoop?.id ?? null} />
          </div>
        )}
      </div>
    );
  } else if (data.making) {
    body = <MakingCard making={data.making} data={data} viewerId={viewerId} names={names} asNext={false} showSinceReview={showSinceReview && !compact} loopId={makingLoop?.id ?? null} />;
  } else {
    body = <NoPlanCard itemsWaiting={itemsWaiting} canStart={canStart} startsWith={startsWith} />;
  }

  return (
    <section aria-labelledby="home-plan" data-testid="home-plan">
      <h2 id="home-plan" className={`${EYEBROW} mb-3`}>The plan</h2>
      {body}
    </section>
  );
}
